import { useState } from 'react';
import {
  cancel_office_booking,
  get_office_booking,
  get_office_bookings,
  resend_office_booking,
} from '../../api';
import { useApi } from '../../use_api';
import { useAuth } from '../../auth/AuthProvider';
import { booking_contact, fmt_dt, status_color } from './format';

export function BookingsView() {
  const { loading, data, error, reload } = useApi(() => get_office_bookings(), []);
  const [selected, setSelected] = useState<string | null>(null);

  if (loading && !data) return <p className="muted">Loading bookings…</p>;
  if (error) return <p className="error">Couldn’t load bookings.</p>;

  const bookings = data?.bookings ?? [];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 16, alignItems: 'start' }}>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Bookings</h2>
        {bookings.length === 0 && <p className="muted">No bookings yet.</p>}
        <div style={{ display: 'grid', gap: 6 }}>
          {bookings.map((b) => (
            <button
              key={b.booking_id}
              onClick={() => setSelected(b.booking_id)}
              style={{
                textAlign: 'left',
                border: '1px solid var(--line)',
                borderRadius: 8,
                padding: '8px 10px',
                background: selected === b.booking_id ? 'var(--green-50)' : '#fff',
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <strong>{b.client_name ?? '(no name)'}</strong>
                <span style={{ color: status_color(b.status), fontSize: 12, fontWeight: 600 }}>{b.status}</span>
              </div>
              <div className="muted" style={{ fontSize: 13 }}>
                {booking_contact(b)} · {fmt_dt(b.timestamp)}
              </div>
            </button>
          ))}
        </div>
      </div>
      {selected && <BookingDetail id={selected} onClose={() => setSelected(null)} onChanged={reload} />}
    </div>
  );
}

function BookingDetail({
  id,
  onClose,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  /** Called after a mutation (e.g. cancel) so the parent list can refetch its rows. */
  onChanged?: () => void;
}) {
  const { state } = useAuth();
  const can_write = state.status === 'signed_in' && state.session.role !== 'member';
  const { loading, data, error, reload } = useApi(() => get_office_booking(id), [id]);
  const [busy, setBusy] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);
  const [cancelMsg, setCancelMsg] = useState<string | null>(null);
  const [cancelled, setCancelled] = useState(false);

  async function resend() {
    setBusy(true);
    setResendMsg(null);
    try {
      const r = await resend_office_booking(id);
      setResendMsg(`Re-sent ${r.resent.length} message${r.resent.length === 1 ? '' : 's'}.`);
    } catch {
      setResendMsg('Re-send failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!window.confirm('Cancel this appointment? This frees the time slot. The customer is not notified automatically.')) {
      return;
    }
    setBusy(true);
    setCancelMsg(null);
    try {
      await cancel_office_booking(id);
      setCancelled(true);
      setCancelMsg('Appointment cancelled.');
      reload(); // refresh this detail's status
      onChanged?.(); // refresh the parent list so the row reflects CANCELLED without a manual reload
    } catch {
      setCancelMsg('Cancel failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (loading && !data) return <div className="card">Loading…</div>;
  if (error || !data) return <div className="card"><p className="error">Couldn’t load this booking.</p></div>;

  const b = data.booking;
  const rows: Array<[string, string | undefined]> = [
    ['Contact', booking_contact(b)],
    ['Email', b.prospect_email],
    ['Address', b.client_address],
    ['Project', b.project_type],
    ['Company', b.prospect_company],
    ['When', fmt_dt(b.timestamp)],
    ['Callback window', b.client_callback_window],
    ['Status', b.status],
  ];

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>{b.client_name ?? 'Booking'}</h3>
        <button aria-label="close" onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18 }}>
          ×
        </button>
      </div>
      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: 14, margin: '12px 0' }}>
        {rows
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k} style={{ display: 'contents' }}>
              <dt className="muted">{k}</dt>
              <dd style={{ margin: 0 }}>{v}</dd>
            </div>
          ))}
      </dl>

      {b.notes && (
        <div style={{ margin: '0 0 12px' }}>
          <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', marginBottom: 4 }}>Notes from the call</div>
          <p style={{ margin: 0, fontSize: 14, whiteSpace: 'pre-wrap' }}>{b.notes}</p>
        </div>
      )}

      {can_write && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn" disabled={busy} onClick={() => void resend()}>
              {busy ? 'Re-sending…' : 'Re-send confirmation'}
            </button>
            {b.status !== 'CANCELLED' && !cancelled && (
              <button className="btn" disabled={busy} onClick={() => void cancel()}>
                Cancel appointment
              </button>
            )}
          </div>
          {resendMsg && <p className="muted" style={{ marginBottom: 0 }}>{resendMsg}</p>}
          {cancelMsg && <p className="muted" style={{ marginBottom: 0 }}>{cancelMsg}</p>}
        </>
      )}

      {data.messages && data.messages.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', marginBottom: 4 }}>Sent messages</div>
          {data.messages.map((m) => (
            <div key={m.message_id} style={{ fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
              <span>{m.channel} → {m.to.join(', ')}</span>
              <span className="muted">{m.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
